import {
  RevenovaTenant,
  ValueLabelOption,
  coolerModeOptions,
  defaultModeOptions,
  greatLakesModeOptions,
  pricingOptions,
  prosponsiveModeOptions,
} from 'pages/QuoteView/LoadBuilding/RevenovaSectionForms/constants';
import { MaybeUndef, Undef } from 'types/UtilityTypes';

/**
 * Determines the tenant from a string by checking if it contains any tenant identifier
 * @param input - The string to check (e.g., tenant name or URL)
 * @returns The matching RevenovaTenant enum value, or undefined if no match found
 */
export const getTenantFromString = (input: string): Undef<RevenovaTenant> => {
  if (!input) {
    return undefined;
  }

  const normalizedInput = input.toLowerCase();

  // Check each tenant value to see if it's contained in the input
  for (const tenant of Object.values(RevenovaTenant)) {
    if (normalizedInput.includes(tenant)) {
      return tenant as RevenovaTenant;
    }
  }

  return undefined;
};

export const tenantToModeOptions: Record<RevenovaTenant, ValueLabelOption[]> = {
  [RevenovaTenant.Prosponsive]: prosponsiveModeOptions,
  [RevenovaTenant.Cooler]: coolerModeOptions,
  [RevenovaTenant.GreatLakes]: greatLakesModeOptions,
};

/**
 * Gets the mode options for a given tenant
 * @param tenant - The tenant string to check
 * @returns The appropriate mode options for the tenant, or default options if no match
 */
export const getModeOptions = (tenant?: string): ValueLabelOption[] => {
  if (!tenant) {
    return defaultModeOptions;
  }

  const detectedTenant = getTenantFromString(tenant);
  if (detectedTenant && tenantToModeOptions[detectedTenant]) {
    return tenantToModeOptions[detectedTenant];
  }

  return defaultModeOptions;
};

/**
 * Maps Salesforce/API `mode` strings to a `mode` value that exists in {@link getModeOptions}
 * for the current tenant (e.g. API returns "Refrigerated" while Cooler Logistics uses "Reefer"
 * in the dropdown).
 */
export function mapApiModeToFormModeValue(
  mode: MaybeUndef<string>,
  tenant?: string
): Undef<string> {
  if (typeof mode !== 'string') {
    return undefined;
  }
  const trimmedMode = mode.trim();
  if (!trimmedMode) {
    return undefined;
  }

  const options = getModeOptions(tenant);
  if (options.some((o) => o.value === trimmedMode)) {
    return trimmedMode;
  }

  const detectedTenant = tenant ? getTenantFromString(tenant) : undefined;
  const lowerCaseMode = trimmedMode.toLowerCase();

  const firstPresent = (...candidates: string[]): Undef<string> => {
    for (const c of candidates) {
      const hit = options.find((o) => o.value === c);
      if (hit) {
        return hit.value;
      }
    }
    return undefined;
  };

  if (
    lowerCaseMode.includes('refrigerate') ||
    lowerCaseMode.includes('reefer')
  ) {
    if (detectedTenant === RevenovaTenant.Cooler) {
      return firstPresent('Reefer', 'Refrigerated');
    }
    if (detectedTenant === RevenovaTenant.GreatLakes) {
      return firstPresent('Reefer', 'Refrigerated');
    }
    if (detectedTenant === RevenovaTenant.Prosponsive) {
      return firstPresent('TL-Refrigerated', 'Refrigerated');
    }
    return firstPresent('Refrigerated', 'Reefer', 'TL-Refrigerated');
  }

  if (lowerCaseMode.includes('flatbed')) {
    if (detectedTenant === RevenovaTenant.Prosponsive) {
      return firstPresent('TL-Flatbed', 'Flatbed');
    }
    return firstPresent('Flatbed', 'TL-Flatbed');
  }

  if (
    (lowerCaseMode.includes('dry') && lowerCaseMode.includes('van')) ||
    lowerCaseMode === 'dryvan'
  ) {
    if (detectedTenant === RevenovaTenant.Prosponsive) {
      return firstPresent('TL-Dry Van', 'Dry Van');
    }
    if (detectedTenant === RevenovaTenant.Cooler) {
      return firstPresent('Truckload', 'TL');
    }
    if (detectedTenant === RevenovaTenant.GreatLakes) {
      return firstPresent('Truckload');
    }
    return firstPresent('Dry Van', 'TL-Dry Van', 'TL');
  }

  if (lowerCaseMode === 'tl' || lowerCaseMode === 'truckload') {
    if (
      detectedTenant === RevenovaTenant.Cooler ||
      detectedTenant === RevenovaTenant.GreatLakes
    ) {
      return firstPresent('Truckload', 'TL');
    }
    return firstPresent('TL', 'Truckload');
  }

  return undefined;
}

/**
 * Normalizes LLM-returned serviceType to a valid pricing option (Spot, Contract, Fixed).
 * Case-insensitive to handle values like 'contract' or 'FIXED' that LLMs may return.
 */
export function normalizeServiceType(serviceType: MaybeUndef<string>): string {
  if (!serviceType || typeof serviceType !== 'string') return 'Spot';
  const trimmedServiceType = serviceType.trim();
  const serviceTypeMatch = pricingOptions.find(
    (o) => o.value.toLowerCase() === trimmedServiceType.toLowerCase()
  );
  return serviceTypeMatch ? serviceTypeMatch.value : 'Spot';
}
